import React, { useEffect, useState } from "react";
import {
    Heading,
    NotFoundMessage,
    UnauthorisedMessage,
} from "@peracto/peracto-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";

import { Link, useParams, Redirect } from "react-router-dom";
import {
    GET_ONE,
    UPDATE,
    useClient,
    getSchemaFromResource,
} from "@peracto/client";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import sharedYupFields from "../../sharedYupFields";

const WarrantyEditContainer = ({ children }) => {
    return (
        <div className="form-container">
            <Heading name="Edit Warranty">
                <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                    <Link className="btn btn-outline-primary" to="/warranty">
                        <FontAwesomeIcon
                            icon={faArrowCircleLeft}
                            className="mr-2"
                        />
                        Back to Warranty Registrations
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    );
};

const WarrantyEdit = ({ WarrantyForm }) => {
    const { client, getResource } = useClient();
    const [loading, setLoading] = useState(true);
    const [unauthorised, setUnauthorised] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [formData, setFormData] = useState(false);
    const [redirect, setRedirect] = useState("");
    const { warrantyId } = useParams();

    const schema = getSchemaFromResource(getResource("warranty")).shape({
        ...sharedYupFields,
    });

    const onSubmit = async (data, actions) => {
        try {
            await client(UPDATE, "warranty-registrations", {
                id: `warranty-registrations/${warrantyId}`,
                data: data,
            });

            actions.setSubmitting(false);

            fetchWarranty();
            toast.success("Warranty information successfully updated!");
        } catch (e) {
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false);
                e.error.body.violations.forEach((error) => {
                    actions.setFieldError(error.propertyPath, error.message);
                });
            }
            console.error(e);
            toast.error(
                e?.error?.body?.hasOwnProperty("hydra:description")
                    ? e.error.body["hydra:description"]
                    : "Whoops, there was a problem...",
            );
            actions.setSubmitting(false);
        }
    };

    const fetchWarranty = async () => {
        try {
            const { data: warrantyData, response: warrantyResponse } =
                await client(GET_ONE, "warranty-registrations", {
                    id: `warranty-registrations/${warrantyId}`,
                });

            const formattedData = warrantyData;

            setFormData(formattedData);

            setLoading(false);

            if (warrantyResponse.status === 404) {
                setRedirect("/warranty-registrations");
            }
        } catch (e) {
            if (e.status === 403) {
                setUnauthorised(true);
            }

            if (e.status === 404) {
                setNotFound(true);
            }

            setLoading(false);
            setRedirect("/warranty-registrations");
        }
    };

    useEffect(() => {
        fetchWarranty();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (unauthorised) {
        return <UnauthorisedMessage />;
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/warranty-registrations"
                message="The warranty information you're looking for could not be found"
                buttonLabel="Go to Warranty Registrations"
            />
        );
    }

    if (redirect) {
        return <Redirect to={redirect} />;
    }

    return loading ? (
        <WarrantyEditContainer>
            <div className="card">
                <div className="card-body">Loading...</div>
            </div>
        </WarrantyEditContainer>
    ) : (
        <>
            {redirect && <Redirect to={redirect} />}

            <Helmet>
                <title>{formData.name || "warranty"} | Edit | Peracto</title>
            </Helmet>

            <div className="form-container">
                <Heading name="Edit Warranty">
                    <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                        <Link
                            className="btn btn-outline-primary"
                            to="/warranty-registrations"
                        >
                            <FontAwesomeIcon
                                icon={faArrowCircleLeft}
                                className="mr-2"
                            />
                            Back to Warranty Registrations
                        </Link>
                    </div>
                </Heading>
                <WarrantyForm
                    modeEdit
                    data={formData}
                    onSubmit={onSubmit}
                    schema={schema}
                />
            </div>
        </>
    );
};

export default WarrantyEdit;
